/**
 * The top-center header trigger for SMS messaging — absolutely positioned
 * in `.shell__header` (see styles.css's `.messaging-pill`), toggling the
 * shared `MessagingPanel` open to the inbox view. Renders nothing at all
 * when messaging isn't configured (`SURGE_API_KEY` unset — see
 * `lib/surge.ts`) or the actor lacks `messaging:read`, same "hide, don't
 * show a broken feature" posture the gear menu's conditional links use for
 * capability gating, applied here to third-party configuration instead.
 */

import { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { api } from '../lib/api.js';
import { useCan } from '../lib/capabilities.js';
import { useMessaging } from '../lib/messaging-context.js';

export function MessagingPill() {
  const canRead = useCan('messaging:read');
  const { state, openInbox, close } = useMessaging();
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    void api<{ configured: boolean }>('/messaging/status')
      .then((data) => {
        if (!cancelled) setConfigured(data.configured);
      })
      .catch(() => {
        // Fails closed — an errored status check hides the pill rather
        // than showing a feature that might not actually work.
      });
    return () => {
      cancelled = true;
    };
  }, [canRead]);

  if (!canRead || !configured) return null;

  const open = state.view !== 'closed';

  return (
    <button
      type="button"
      className={open ? 'messaging-pill messaging-pill--active' : 'messaging-pill'}
      aria-expanded={open}
      onClick={() => (open ? close() : openInbox())}
    >
      <MessageCircle size={16} strokeWidth={1.8} aria-hidden="true" />
      SMS Messaging
    </button>
  );
}
